import { takeLatest } from 'redux-saga';
import { put, call, fork, select } from 'redux-saga/effects';
import { fromJS } from 'immutable';

import { getUserId } from '../../selectors/user-selectors';
import { getPermissionId } from '../../selectors/campaign-selectors';
import { createTrackedAsset } from '../../utils/jsonapi-functions/tracked-assets';
import { extractAssetURLData } from '../../utils/asset-url-helper-service';
import { updateDiscussionTrackedAssets } from '../../utils/jsonapi-functions/discussions';
import { updateCampaignTrackedAssets } from '../../utils/jsonapi-functions/campaigns';
import { updateDeliverableIsLive } from '../../utils/jsonapi-functions/deliverables';
import { reportError } from '../../utils/error-handling-service';
import { timeout } from '../../utils/helper-functions';
import { USER, CAMPAIGN, DISCUSSION_PAGE } from '../../utils/static-configuration';
import { ADD_TRACKED_ASSET } from './constants';
import { GET_NEW_ASSET } from '../../containers/analytics-asset-list/constants';
import { GET_NEW_CAMPAIGN_ANALYTICS } from '../../containers/campaign-analytics/constants';
import { MODAL_LOADING, MODAL_ERROR } from '../../constants/modal';
import { getData } from '../../selectors/track-asset-modal-selectors';
import { UPDATE_DELIVERABLE_IS_LIVE, SEND_MESSAGE } from '../../containers/discussion/constants';

// Adds a tracked video if the URL is valid
export function* addTrackedVideo(action) {
  try {
    // When this saga is used within a modal set loading states, don't clear the modal as this may not be the last saga to call
    yield put({ type: MODAL_LOADING, loading: true });

    const { data } = action;

    const callback = action.callback || null;
    const assetURL = data.assetURL;
    const userId = yield select(getUserId);
    const campaignId = data.campaignId || null;
    const discussionId = data.discussionId || null;
    const discussionName = data.discussionName || null;
    const assetTypeData = data.assetTypeData || extractAssetURLData(assetURL);

    if (assetTypeData) {
      const trackedAssetData = {
        ...assetTypeData,
        userId,
        createdSource: campaignId ? CAMPAIGN : USER,
      };
      if (campaignId) trackedAssetData.campaignId = campaignId;
      if (discussionId) trackedAssetData.discussionId = discussionId;
      if (discussionName) trackedAssetData.discussionName = discussionName;

      const newTrackedAsset = yield call(createTrackedAsset, trackedAssetData);

      // Collaboration stream adding tracked asset
      if (discussionId && campaignId) {
        yield call(updateDiscussionTrackedAssets, discussionId, newTrackedAsset);

        // Update the deliverable to being live
        const modalData = yield select(getData);

        const deliverableId = modalData.get('deliverableId');
        yield call(updateDeliverableIsLive, deliverableId, { live: true });

        yield put({ type: UPDATE_DELIVERABLE_IS_LIVE, deliverableId, live: true });

        const permissionId = yield select(getPermissionId);
        const message = assetTypeData.assetURL;
        const metaData = {
          messageType: 'tracked-asset',
          permissionId,
          discussionId,
          deliverableId,
        };
        const messageData = {
          message,
          metaData,
          page: DISCUSSION_PAGE,
        };
        yield put({ type: SEND_MESSAGE, data: messageData });
      }
      // Campaign analytics tab adding tracked asset
      if (campaignId && !discussionId) {
        yield call(updateCampaignTrackedAssets, campaignId, newTrackedAsset);
        yield call(timeout, 3000);
        yield put({ type: GET_NEW_CAMPAIGN_ANALYTICS, data: { trackedAsset: newTrackedAsset.toJSONTree() } });
      }
      // Analytics page adding tracked asset
      if (!discussionId && !campaignId) {
        yield call(timeout, 3000);
        yield put({ type: GET_NEW_ASSET, data: { trackedAsset: newTrackedAsset.toJSONTree() } });
      }
      if (callback) callback();
    }
    if (!assetTypeData && !discussionId && !campaignId) yield put({ type: MODAL_ERROR, error: 'Invalid asset URL' });

  } catch (error) {
    reportError('ADD_TRACKED_ASSET_ERROR', error.message);
    yield put({ type: MODAL_ERROR, error });
  }
}

export function* watchAddTrackedVideo() {
  yield takeLatest(ADD_TRACKED_ASSET, addTrackedVideo);
}

export default function* trackedVideoSagas() {
  yield [
    fork(watchAddTrackedVideo),
  ];
}



/** WEBPACK FOOTER **
 ** ./src/containers/track-asset-modal/sagas.js
 **/